/**
 * AiFlagToggle — Ein-Klick-Toggle für `is_ai_generated` an einem Medium.
 *
 * Zwei Darstellungs-Varianten:
 *  - `default` (Pill): kompakter Gold-Pill mit ✨-Icon, AN/AUS-Chip, Info-ⓘ,
 *    Save-Status (Spinner/✓/⚠) und optionalem Auto-Save-Hinweis.
 *    Für Media-Slot-Previews im CMS-Editor.
 *  - `icon` (Icon-Only): kleiner runder ✨-Button für sehr enge Grid-Kacheln
 *    (Mediengalerie, Projekt-Media-Raster). Gold-aktiv vs. dunkel-inaktiv.
 *
 * Auto-Save: PATCH /api/admin/media/{id} mit `{is_ai_generated: bool}`.
 * Optimistic-Update via `setAiFlagLocal` (informiert alle Konsumenten
 * inkl. SmartMedia + BeforeAfterSlider-Overlays via aiFlags-Cache-Broadcast).
 */
import { useEffect, useRef, useState } from "react";
import { Sparkles, Info, Loader2, Check, AlertCircle } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/auth";
import { useMediaAiFlags, setAiFlagLocal } from "@/lib/aiFlags";

type Status = "idle" | "saving" | "saved" | "error";

type Props = {
    /** Media-ID (`media.id`), für die das AI-Flag getoggled wird. */
    mediaId: string | undefined | null;
    /** Kompakter Pill-Look (z. B. für kleine Slot-Previews). Default: false. */
    compact?: boolean;
    /** Darstellungs-Variante:
     *  - `default`: voller Pill mit Label „KI AN/AUS" + Info-ⓘ + Auto-Save-Status.
     *  - `icon`: nur runder ✨-Icon-Button (für sehr enge Grid-Kacheln). */
    variant?: "default" | "icon";
    /** Falls true: unter dem Toggle erscheint der Hinweis „Änderungen werden
     *  automatisch gespeichert." Nur einmal im Editor sinnvoll. */
    showAutoSaveHint?: boolean;
    /** Zusätzliche Klassen. */
    className?: string;
    /** data-testid Präfix — default `ai-flag-toggle`. */
    testIdPrefix?: string;
};

export default function AiFlagToggle({
    mediaId,
    compact = false,
    variant = "default",
    showAutoSaveHint = false,
    className,
    testIdPrefix = "ai-flag-toggle",
}: Props) {
    const flags = useMediaAiFlags([mediaId || undefined]);
    const active = mediaId ? flags[mediaId] === true : false;
    const [status, setStatus] = useState<Status>("idle");
    const [showTip, setShowTip] = useState(false);
    const tipTimer = useRef<number | null>(null);
    const statusTimer = useRef<number | null>(null);

    useEffect(() => {
        return () => {
            if (tipTimer.current) window.clearTimeout(tipTimer.current);
            if (statusTimer.current) window.clearTimeout(statusTimer.current);
        };
    }, []);

    if (!mediaId) return null;

    const persist = async (next: boolean) => {
        if (statusTimer.current) window.clearTimeout(statusTimer.current);
        setStatus("saving");
        setAiFlagLocal(mediaId, next);
        const res = await api<{ id: string; is_ai_generated?: boolean }>(
            `/api/admin/media/${mediaId}`,
            {
                method: "PATCH",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ is_ai_generated: next }),
            },
        );
        if (!res.ok) {
            setAiFlagLocal(mediaId, !next);
            setStatus("error");
            toast.error(res.error || "Konnte KI-Kennzeichnung nicht speichern.");
            statusTimer.current = window.setTimeout(() => setStatus("idle"), 2500);
            return;
        }
        setStatus("saved");
        toast.success(next ? "Als KI-generiert markiert." : "KI-Markierung entfernt.");
        statusTimer.current = window.setTimeout(() => setStatus("idle"), 1500);
    };

    const onToggle = () => {
        if (status === "saving") return;
        void persist(!active);
    };
    const onRetry = () => {
        if (status !== "error") return;
        void persist(active);
    };

    const openTip = () => {
        if (tipTimer.current) window.clearTimeout(tipTimer.current);
        setShowTip(true);
    };
    const closeTipSoon = () => {
        if (tipTimer.current) window.clearTimeout(tipTimer.current);
        tipTimer.current = window.setTimeout(() => setShowTip(false), 150);
    };

    /* ─── Icon-Only-Variante (für kleine Grid-Kacheln) ─── */
    if (variant === "icon") {
        // Sichtbarer Pill: 16px hoch, kompakt. Der Button selbst hat
        // transparentes Outer-Padding von 4 px, wodurch die effektive
        // Klick-Fläche 24×24 px erreicht (Barrierefreiheit / Touch-Target).
        // Aktiv: Gold-Pill mit „KI"-Text neben Sparkles.
        // Inaktiv: reines Icon (kleiner dunkler Kreis mit Gold-Rand).
        const hasLabel = active || status === "saved";
        return (
            <button
                type="button"
                onClick={(e) => { e.stopPropagation(); e.preventDefault(); onToggle(); }}
                disabled={status === "saving"}
                aria-label={active ? "KI-Kennzeichnung ausschalten" : "KI-Kennzeichnung einschalten"}
                aria-pressed={active}
                title={active ? "KI-generiert (Klick: Markierung entfernen)" : "Nicht KI-markiert (Klick: als KI markieren)"}
                data-testid={testIdPrefix}
                className={`inline-flex items-center justify-center transition-colors ${className || ""}`}
                style={{
                    padding: 4, // unsichtbare Hit-Area für Touch-Target ≥ 24 px
                    background: "transparent",
                    border: "none",
                    cursor: status === "saving" ? "wait" : "pointer",
                    opacity: status === "saving" ? 0.7 : 1,
                }}
            >
                <span
                    className="inline-flex items-center justify-center gap-0.5 rounded-full"
                    style={{
                        height: 16,
                        minWidth: 16,
                        padding: hasLabel ? "0 5px 0 4px" : 0,
                        background: active ? "hsl(var(--hf-gold-dark))" : "rgba(23,22,19,0.82)",
                        color: active ? "hsl(var(--hf-bg))" : "#EBD5A0",
                        border: `1px solid ${active ? "hsl(var(--hf-gold))" : "rgba(215,170,100,0.55)"}`,
                        boxShadow: active
                            ? "0 0 0 1px rgba(215,170,100,0.22), 0 1px 3px rgba(0,0,0,0.28)"
                            : "0 1px 3px rgba(0,0,0,0.28)",
                        fontSize: 8,
                        fontWeight: 700,
                        letterSpacing: "0.12em",
                        textTransform: "uppercase",
                    }}
                >
                    {status === "saving" ? (
                        <Loader2 size={9} strokeWidth={2.4} className="animate-spin" />
                    ) : status === "saved" ? (
                        <>
                            <Check size={9} strokeWidth={2.6} />
                            <span>ok</span>
                        </>
                    ) : status === "error" ? (
                        <AlertCircle size={9} strokeWidth={2.2} style={{ color: "#F26D6D" }} />
                    ) : (
                        <>
                            <Sparkles size={9} strokeWidth={2.2} />
                            {active && <span>KI</span>}
                        </>
                    )}
                </span>
            </button>
        );
    }

    /* ─── Default-Pill-Variante ─── */
    return (
        <div
            className={`relative inline-flex flex-col items-start gap-1 ${className || ""}`}
            data-testid={`${testIdPrefix}-wrap`}
        >
            <div className="inline-flex items-center gap-1">
                <button
                    type="button"
                    onClick={onToggle}
                    disabled={status === "saving"}
                    aria-label={active ? "KI-Kennzeichnung ausschalten" : "KI-Kennzeichnung einschalten"}
                    aria-pressed={active}
                    data-testid={testIdPrefix}
                    className="inline-flex items-center gap-1 rounded-full transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[hsl(var(--hf-gold-dark))]"
                    style={{
                        padding: compact ? "2px 5px" : "2px 6px",
                        fontSize: compact ? 8 : 9,
                        fontWeight: 600,
                        letterSpacing: "0.12em",
                        textTransform: "uppercase",
                        background: active ? "hsl(var(--hf-gold-dark))" : "hsl(var(--hf-surface))",
                        color: active ? "hsl(var(--hf-bg))" : "hsl(var(--hf-muted))",
                        border: `1px solid ${active ? "hsl(var(--hf-gold-dark))" : "hsl(var(--hf-line))"}`,
                        cursor: status === "saving" ? "wait" : "pointer",
                        opacity: status === "saving" ? 0.8 : 1,
                    }}
                >
                    <Sparkles size={compact ? 8 : 9} strokeWidth={2.2} aria-hidden />
                    <span>KI</span>
                    <span
                        aria-hidden
                        style={{
                            display: "inline-flex",
                            alignItems: "center",
                            justifyContent: "center",
                            marginLeft: 1,
                            width: compact ? 10 : 12,
                            height: compact ? 10 : 12,
                            borderRadius: 999,
                            background: active
                                ? "hsl(var(--hf-bg) / 0.22)"
                                : "hsl(var(--hf-line))",
                            color: active ? "hsl(var(--hf-bg))" : "hsl(var(--hf-muted))",
                            fontSize: 7,
                            fontWeight: 700,
                            letterSpacing: 0,
                        }}
                    >
                        {active ? "AN" : "AUS"}
                    </span>
                </button>

                {/* Save-Status: Spinner / ✓ / ⚠ */}
                {status === "saving" && (
                    <span
                        className="inline-flex items-center"
                        style={{ color: "hsl(var(--hf-muted))" }}
                        aria-live="polite"
                        data-testid={`${testIdPrefix}-status-saving`}
                    >
                        <Loader2 size={compact ? 10 : 11} strokeWidth={2.2} className="animate-spin" />
                    </span>
                )}
                {status === "saved" && (
                    <span
                        className="inline-flex items-center gap-0.5 tabular-nums"
                        style={{
                            color: "#4A8A4A",
                            fontSize: compact ? 8 : 9,
                            fontWeight: 500,
                            transition: "opacity 200ms ease",
                        }}
                        aria-live="polite"
                        data-testid={`${testIdPrefix}-status-saved`}
                    >
                        <Check size={compact ? 10 : 11} strokeWidth={2.6} />
                        gespeichert
                    </span>
                )}
                {status === "error" && (
                    <button
                        type="button"
                        onClick={onRetry}
                        className="inline-flex items-center gap-1 underline decoration-dotted"
                        style={{ color: "#C24545", fontSize: compact ? 9 : 10 }}
                        data-testid={`${testIdPrefix}-status-error`}
                        title="Erneut versuchen"
                    >
                        <AlertCircle size={compact ? 10 : 11} strokeWidth={2.2} />
                        Fehler – erneut versuchen
                    </button>
                )}

                {/* Info-Popover-Trigger */}
                <button
                    type="button"
                    onMouseEnter={openTip}
                    onMouseLeave={closeTipSoon}
                    onFocus={openTip}
                    onBlur={closeTipSoon}
                    onClick={(e) => { e.stopPropagation(); setShowTip((v) => !v); }}
                    aria-label="Info zur KI-Kennzeichnung"
                    aria-expanded={showTip}
                    data-testid={`${testIdPrefix}-info`}
                    className="inline-flex items-center justify-center rounded-full transition-colors"
                    style={{
                        width: compact ? 12 : 14,
                        height: compact ? 12 : 14,
                        background: "transparent",
                        color: "hsl(var(--hf-muted))",
                        border: "1px solid hsl(var(--hf-line))",
                    }}
                >
                    <Info size={compact ? 8 : 9} strokeWidth={2} />
                </button>
            </div>

            {showAutoSaveHint && (
                <span
                    className="italic"
                    style={{ color: "hsl(var(--hf-muted))", fontSize: 9 }}
                    data-testid={`${testIdPrefix}-autosave-hint`}
                >
                    Änderungen werden automatisch gespeichert.
                </span>
            )}

            {showTip && (
                <span
                    role="tooltip"
                    onMouseEnter={openTip}
                    onMouseLeave={closeTipSoon}
                    className="absolute z-30 text-xs leading-relaxed"
                    style={{
                        top: "calc(100% + 6px)",
                        left: 0,
                        width: 260,
                        background: "#FFFDF9",
                        border: "1px solid #D8CDBF",
                        borderRadius: 8,
                        padding: "10px 12px",
                        color: "#4A453F",
                        boxShadow: "0 6px 20px rgba(20,12,4,0.08), 0 2px 4px rgba(20,12,4,0.05)",
                    }}
                    data-testid={`${testIdPrefix}-tooltip`}
                >
                    Die KI-Kennzeichnung gilt <strong>global für dieses Medium</strong> — sobald es
                    irgendwo eingesetzt wird, erscheint das ✨-Badge auf der öffentlichen Seite.
                </span>
            )}
        </div>
    );
}
